import type { LocationQueryRaw, RouteLocationNormalizedLoaded, RouteLocationRaw, RouteRecordName } from 'vue-router'

export const ConfigShareRouteName = {
  workspace: 'driver-workspace',
  home: 'config-share-home',
  publish: 'config-share-publish',
  mine: 'config-share-mine',
  user: 'config-share-user',
} as const

const RETURN_ROUTE_NAME_KEY = 'returnRouteName'
const RETURN_USER_ID_KEY = 'returnUserId'
const LEGACY_FROM_KEY = 'from'
const LEGACY_WORKSPACE_FROM = 'workspace'

const listRouteNames = new Set<RouteRecordName>([ConfigShareRouteName.home, ConfigShareRouteName.mine, ConfigShareRouteName.user])

const queryString = (value: unknown): string | undefined => {
  return typeof value === 'string' && value ? value : undefined
}

export const configShareListQuery = (route: RouteLocationNormalizedLoaded): LocationQueryRaw => ({
  keyword: queryString(route.query.keyword),
  sort: queryString(route.query.sort),
  scope: queryString(route.query.scope),
})

export const isConfigShareListRoute = (name: RouteRecordName | null | undefined): boolean => {
  return name ? listRouteNames.has(name) : false
}

export const configShareListLocationFromRoute = (route: RouteLocationNormalizedLoaded): RouteLocationRaw | null => {
  const query = configShareListQuery(route)

  if (route.name === ConfigShareRouteName.home) {
    return { name: ConfigShareRouteName.home, query }
  }

  if (route.name === ConfigShareRouteName.mine) {
    return { name: ConfigShareRouteName.mine, query }
  }

  if (route.name === ConfigShareRouteName.user) {
    const userId = queryString(route.params.userId)
    return userId ? { name: ConfigShareRouteName.user, params: { userId }, query } : null
  }

  return null
}

export const configSharePublishQuery = (route: RouteLocationNormalizedLoaded, profileIndex?: number): LocationQueryRaw => {
  const query: LocationQueryRaw = {
    ...configShareListQuery(route),
    profileIndex: profileIndex === undefined ? queryString(route.query.profileIndex) : String(profileIndex),
  }

  const listLocation = configShareListLocationFromRoute(route)
  if (listLocation && typeof listLocation === 'object' && 'name' in listLocation) {
    query[RETURN_ROUTE_NAME_KEY] = String(listLocation.name)
    if (listLocation.name === ConfigShareRouteName.user && typeof listLocation.params === 'object') {
      query[RETURN_USER_ID_KEY] = String(listLocation.params.userId || '')
    }
    return query
  }

  if (route.name === ConfigShareRouteName.workspace || route.query[LEGACY_FROM_KEY] === LEGACY_WORKSPACE_FROM) {
    query[RETURN_ROUTE_NAME_KEY] = ConfigShareRouteName.workspace
  }

  return query
}

export const configShareWorkspacePublishQuery = (profileIndex: number): LocationQueryRaw => ({
  profileIndex: String(profileIndex),
  [RETURN_ROUTE_NAME_KEY]: ConfigShareRouteName.workspace,
})

export const configShareReturnLocation = (route: RouteLocationNormalizedLoaded): RouteLocationRaw => {
  const returnRouteName = queryString(route.query[RETURN_ROUTE_NAME_KEY])

  if (returnRouteName === ConfigShareRouteName.workspace || route.query[LEGACY_FROM_KEY] === LEGACY_WORKSPACE_FROM) {
    return { name: ConfigShareRouteName.workspace }
  }

  if (returnRouteName === ConfigShareRouteName.mine) {
    return { name: ConfigShareRouteName.mine, query: configShareListQuery(route) }
  }

  if (returnRouteName === ConfigShareRouteName.user) {
    const userId = queryString(route.query[RETURN_USER_ID_KEY])
    if (userId) {
      return { name: ConfigShareRouteName.user, params: { userId }, query: configShareListQuery(route) }
    }
  }

  return { name: ConfigShareRouteName.home, query: configShareListQuery(route) }
}
